import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { communityService, invitationService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';

function CommunityCard({ community: c, actionLabel = 'View', onAction, onClick }) {
  return (
    <div
      onClick={onClick}
      className="bg-surface border border-outline rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
    >
      <div className="h-24 bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center overflow-hidden">
        {c.imageId ? (
          <img alt={c.name} className="w-full h-full object-cover" src={getImageUrl(c.imageId)} />
        ) : (
          <span className="material-symbols-outlined text-4xl text-primary/60">group_work</span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-base text-on-surface truncate">{c.name}</h3>
        {c.description && (
          <p className="text-sm text-on-surface-variant mt-1 line-clamp-2">{c.description}</p>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-on-surface-variant flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">people</span>
            {c.memberCount || 0} members
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onAction?.(); }}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-on-primary hover:opacity-90 transition-opacity"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const ExploreCommunities = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const theme = getThemeVars(ACTIVE_THEME);

  const { data: userCommunities = [], isLoading: userCommunitiesLoading } = useQuery({
    queryKey: ['userCommunities'],
    enabled: !authLoading,
    queryFn: async () => { const r = await communityService.getUserCommunities(); return r?.data || []; },
  });

  const { data: availableCommunities = [], isLoading: availableLoading } = useQuery({
    queryKey: ['availableCommunities'],
    enabled: !authLoading,
    queryFn: async () => { const r = await communityService.getAvailableToJoin(); return r?.data || []; },
  });

  const { data: pendingInvitations = [], isLoading: invitationsLoading } = useQuery({
    queryKey: ['pendingCommunityInvitations'],
    enabled: !authLoading,
    queryFn: async () => {
      const r = await invitationService.getPending();
      const invitations = r?.data || [];
      const detailed = await Promise.allSettled(
        invitations.map(async (inv) => {
          if (!inv?.token) return inv;
          const dr = await invitationService.getByToken(inv.token);
          return { ...inv, ...(dr?.data || {}) };
        })
      );
      return detailed.map((res, i) => res.status === 'fulfilled' ? res.value : invitations[i]);
    },
  });

  const joinMutation = useMutation({
    mutationFn: ({ communityId, userId }) => communityService.joinCommunity(communityId, userId),
    onSuccess: async () => {
      // Invalidamos también el feed de la home y la actividad reciente para
      // que al volver al dashboard aparezcan los productos de la comunidad
      // recién unida sin necesidad de refrescar a mano.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userCommunities'] }),
        queryClient.invalidateQueries({ queryKey: ['availableCommunities'] }),
        queryClient.invalidateQueries({ queryKey: ['productCards'] }),
        queryClient.invalidateQueries({ queryKey: ['recentActivity'] }),
      ]);
    },
  });

  const handleJoin = (communityId) => {
    if (!user?.id) { navigate('/login'); return; }
    joinMutation.mutate({ communityId, userId: user.id });
  };

  const isLoading = authLoading || userCommunitiesLoading || availableLoading || invitationsLoading;

  if (isLoading) {
    return (
      <div
        className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
        style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
      >
        <DashboardSidebar activePath="/communities" />
        <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
          <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 mobile-sm:grid-cols-2 tablet-md:grid-cols-3 desktop-xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-surface border border-outline rounded-lg overflow-hidden animate-pulse">
                  <div className="h-24 bg-surface-container-high" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-surface-container-high rounded w-3/4" />
                    <div className="h-3 bg-surface-container-high rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const isEmpty = userCommunities.length === 0 && pendingInvitations.length === 0 && availableCommunities.length === 0;

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/communities" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-on-surface">{t('Communities')}</h1>
        <button
          onClick={() => navigate('/communities/create')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          {t('Create')}
        </button>
      </div>

      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="material-symbols-outlined text-6xl text-outline-variant mb-4">groups</span>
          <h3 className="text-lg font-bold text-on-surface mb-2">{t('No communities yet')}</h3>
          <p className="text-sm text-on-surface-variant mb-6 max-w-sm">
            {t('Join a community through an invitation, email domain, or create your own.')}
          </p>
          <button
            onClick={() => navigate('/communities/create')}
            className="px-4 py-2 rounded-lg bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity"
          >
            {t('Create Community')}
          </button>
        </div>
      )}

      {/* Your Communities */}
      {userCommunities.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-on-surface mb-4">{t('Your Communities')}</h2>
          <div className="grid grid-cols-1 mobile-sm:grid-cols-2 tablet-md:grid-cols-3 desktop-xl:grid-cols-4 gap-4">
            {userCommunities.map((c) => (
              <CommunityCard
                key={c.id}
                community={c}
                actionLabel={t('View')}
                onClick={() => navigate(`/communities/${c.id}`)}
                onAction={() => navigate(`/communities/${c.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
            {t('Invitations')}
            <span className="bg-primary text-on-primary text-xs px-2 py-0.5 rounded-full">{pendingInvitations.length}</span>
          </h2>
          <div className="grid grid-cols-1 mobile-sm:grid-cols-2 tablet-md:grid-cols-3 gap-4">
            {pendingInvitations.map((inv) => (
              <div key={inv.id} className="bg-surface border border-outline rounded-lg p-4 shadow-sm">
                <p className="text-sm font-semibold text-on-surface mb-1">
                  {inv.communityName || t('Community')}
                </p>
                {inv.inviterName && (
                  <p className="text-xs text-on-surface-variant mb-3">{t('From')}: {inv.inviterName}</p>
                )}
                {inv.message && (
                  <p className="text-xs text-on-surface-variant italic mb-3">"{inv.message}"</p>
                )}
                <button
                  onClick={() => navigate(`/invitation?token=${encodeURIComponent(inv.token)}`)}
                  className="w-full py-2 rounded-lg bg-primary text-on-primary text-xs font-semibold hover:opacity-90 transition-opacity"
                >
                  {t('Review Invitation')}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Available to Join */}
      {availableCommunities.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-on-surface mb-4">{t('Discover')}</h2>
          <div className="grid grid-cols-1 mobile-sm:grid-cols-2 tablet-md:grid-cols-3 desktop-xl:grid-cols-4 gap-4">
            {availableCommunities.map((c) => (
              <CommunityCard
                key={c.id}
                community={c}
                actionLabel={joinMutation.isPending && joinMutation.variables?.communityId === c.id ? t('Joining...') : t('Join')}
                onClick={() => navigate(`/communities/${c.id}`)}
                onAction={() => handleJoin(c.id)}
              />
            ))}
          </div>
        </section>
      )}

      {joinMutation.isError && (
        <div className="mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {t('Error joining community')}: {joinMutation.error?.response?.data?.message || joinMutation.error?.message}
        </div>
      )}
        </div>
      </main>
    </div>
  );
};

export default ExploreCommunities;
